import { useEffect, useMemo, useState } from 'react'
import { usePageContext } from '../pages/[[...path]]'

type UseTranslationOptions = {
	/** Value used before the translation is loaded or if none exists */
	fallback?: string
}

export function useTranslation(key: string, options?: UseTranslationOptions): string {
	const { locale } = usePageContext()
	const initial = useMemo(() => options?.fallback ?? key, [options?.fallback, key])
	const [value, setValue] = useState<string>(initial)

	useEffect(() => {
		let cancelled = false
		async function load() {
			try {
				const apiUrl = process.env.NEXT_PUBLIC_API_URL
				const token = process.env.NEXT_PUBLIC_TOKEN
				if (!apiUrl || !token || !locale) {
					return
				}
				const query = `
					query ($key: String!, $code: String!) {
						listTranslationLocale(
							filter: { root: { key: { eq: $key } }, locale: { code: { eq: $code } } }
							limit: 1
						) {
							value
						}
					}
				`
				const response = await fetch(apiUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						query,
						variables: { key, code: locale },
					}),
				})
				if (!response.ok) {
					return
				}
				const json = await response.json()
				const next = json?.data?.listTranslationLocale?.[0]?.value as string | undefined
				if (!cancelled && next) {
					setValue(next)
				}
			} catch {
				// silently ignore errors
			}
		}
		setValue(initial)
		load()
		return () => {
			cancelled = true
		}
	}, [key, locale, initial])

	return value
}
